import React, { useCallback, useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../components/Common/BreadCrumb";
import useAuthUser from "../../components/Hooks/useAuthUser";
import { getCurrentMonth, getMyPlanUsage } from "../Usage/usageApi";
import {
  CountInfoTip,
  RecentUsageTable,
  UsageBar,
  planLabel,
  quotaLabel,
} from "../Usage/UsageParts";
import { formatDate } from "../Usage/usageFormat";

const TENANT_ROLES = ["TENANT_ADMIN", "TENANT_USER"];

// The tenant's own plan and usage (TENANT_ADMIN, TENANT_USER).
// No unit price, overage amount or contract start date.
const PlanUsage = () => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const { roles } = useAuthUser();
  const isTenant = (roles || []).some((role) => TENANT_ROLES.includes(role));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  document.title = t("common.pageTitle", { page: t("usage.tenant.pageTitle") });

  const load = useCallback(async () => {
    if (!isTenant) return;
    setLoading(true);
    setError(false);
    try {
      setData(await getMyPlanUsage());
    } catch (err) {
      setData(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [isTenant]);

  useEffect(() => {
    load();
  }, [load]);

  if (!isTenant) return <Redirect to="/usage" />;

  const thisMonth = data?.months.find((m) => m.month === getCurrentMonth());

  // One card, same structure as the admin card on the tenant details page.
  const renderContent = () => (
    <Card>
      <CardHeader>
        <h5 className="card-title mb-0">{t("usage.tenant.pageTitle")}</h5>
      </CardHeader>
      <CardBody>
        {!data.plan ? (
          <Alert color="info" className="d-flex align-items-center gap-2 mb-4">
            <i className="ri-information-line fs-16"></i>
            <span>{t("usage.tenant.planNotSet")}</span>
          </Alert>
        ) : (
          // Same layout and text sizes as the tenant details page: plan,
          // quota and contract on the left, this month's usage on the right.
          <Row className="g-4 mb-4">
            <Col lg={6}>
              <Row className="g-3">
                <Col xs={6}>
                  <p className="text-muted mb-1">{t("usage.tenant.plan")}</p>
                  <p className="fw-semibold fs-15 mb-0">
                    {planLabel(t, data.plan)}
                  </p>
                </Col>
                <Col xs={6}>
                  <p className="text-muted mb-1">{t("usage.tenant.quota")}</p>
                  <p className="fw-semibold fs-15 mb-0">
                    {quotaLabel(t, data.quota, lng)}
                  </p>
                </Col>
                <Col xs={12}>
                  <p className="text-muted mb-1">
                    {t("usage.tenant.contractEnd")}
                  </p>
                  <p className="fw-semibold fs-15 mb-1">
                    {formatDate(data.contractEnd, lng)}
                  </p>
                  <p className="text-muted mb-0 fs-12">
                    {t("usage.tenant.renewalNote")}
                  </p>
                </Col>
              </Row>
            </Col>
            {thisMonth && (
              <Col lg={6}>
                <p className="text-muted mb-1">
                  {t("usage.tenant.used")}
                  <CountInfoTip />
                </p>
                <UsageBar
                  messageCount={thisMonth.messageCount}
                  quota={thisMonth.quota}
                  showCounts
                  large
                />
              </Col>
            )}
          </Row>
        )}

        {/* Last 6 months */}
        <h6 className="mb-3">{t("usage.recent.heading")}</h6>
        <RecentUsageTable months={data.months} />
      </CardBody>
    </Card>
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("usage.tenant.pageTitle")}
          pageTitle={t("nav.settings")}
        />
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : error ? (
          <Alert
            color="danger"
            className="d-flex justify-content-between align-items-center"
          >
            <span>{t("usage.loadFailed")}</span>
            <Button color="danger" outline size="sm" onClick={load}>
              {t("common.actions.retry")}
            </Button>
          </Alert>
        ) : (
          renderContent()
        )}
      </Container>
    </div>
  );
};

export default PlanUsage;
